import * as React from 'react';

import { Input } from '@built/design-system';
import { Field } from '@built/built-components';

import * as styles from './SSOAliasField.module.scss';
import { getValidationError } from '../utils';
import l10n from '@built/i18n';
import { ValidationErrorType } from 'src/types';

/**
 * Renders an sso alias field with a validation indicator
 */
export default function SSOAliasField(props: SSOAliasFieldProps) {
  const { className, showError, languageReady } = props;
  const classes = [styles.field] as string[];
  let subtext = '';
  let inputLabel = languageReady
    ? l10n.t('SSOAliasField:info.label', 'Organization ID')
    : 'Organization ID';
  const tooltip = l10n.t(
    'LoginSso:Notification.Alias.tooltip',
    "By entering your Organization's ID, you are redirected to your organization's single sign-on login page."
  );

  if (className) {
    classes.push(className);
  }

  // if user has provided some input and it's invalid
  // we need to toggle the error state for the SSOAliasField
  // and add some subtext to prompt the user
  if (showError) {
    classes.push(styles.error);
    subtext = getValidationError(ValidationErrorType.VALIDATION_ALIAS_ERROR);
  }

  return (
    <React.Fragment>
      <Field className={classes.join(' ')} tooltip={tooltip} label={inputLabel}>
        <Input
          disabled={props.disabled}
          autoFocus={props.autoFocus}
          name={props.name}
          onChange={props.onChange}
          size="lg"
          value={props.value}
          subtext={subtext}
        />
      </Field>
    </React.Fragment>
  );
}

export interface SSOAliasFieldProps {
  /** An additional class name to provide custom styling */
  className?: string;

  /** Whether the input should be disabled */
  disabled?: boolean;

  /** Whether the input should be focused as soon as it is rendered */
  autoFocus?: boolean;

  /** Name of the SSOAliasField input element */
  name: string;

  showError?: boolean;

  /** SSOAliasField input value */
  value: string;

  /** Is the alias valid? */
  validAlias: boolean;

  /** Flag to determine if translations are ready */
  languageReady: boolean;

  /** Callback to handle updates to the current email value */
  onChange(event: React.SyntheticEvent<HTMLInputElement>): void;

  /** Track snowplow event when a user clicks "Login with SSO" */
  trackLoginSSO?(): void;
}
